import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useGiftRequestStatistics } from '@/hooks/useGiftsRequests';
import ReactECharts from 'echarts-for-react';
import { numberFormat, currencyFormat } from './ChartUtils';
import { Gift, TrendingUp, DollarSign } from 'lucide-react';
import LoadingCircle from '@/components/LoadingCircle';

interface GiftRequestChartProps {
  selectedYear: number;
  onYearChange: (year: number) => void;
}

interface MonthlyData {
  month: number;
  monthName: string;
  totalRequests: number;
  totalGiftsGiven: number;
  totalValue: number;
}

interface ChartParam {
  name: string;
  seriesName: string;
  value: number;
  color: string;
}

const getGiftRequestChartOption = (monthlyData: MonthlyData[]) => {
  const months = monthlyData.map((item) => item.monthName);
  const requests = monthlyData.map((item) => item.totalRequests);
  const giftsGiven = monthlyData.map((item) => item.totalGiftsGiven);
  const values = monthlyData.map((item) => item.totalValue);

  return {
    title: {
      text: 'Thống Kê Yêu Cầu Quà Tặng',
      left: 'center',
      textStyle: {
        color: '#1f2937',
        fontSize: 16,
        fontWeight: 'bold',
      },
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'cross',
        crossStyle: {
          color: '#999',
        },
      },
      formatter: function (params: ChartParam[]) {
        let result = `<div style="font-weight: bold; margin-bottom: 8px;">${params[0].name}</div>`;
        params.forEach((param: ChartParam) => {
          const color = param.color;
          const value =
            param.seriesName === 'Giá trị (VNĐ)'
              ? currencyFormat(param.value).replace('₫', ' VNĐ')
              : numberFormat(param.value);
          result += `
            <div style="display: flex; align-items: center; margin: 4px 0;">
              <span style="display: inline-block; width: 12px; height: 12px; background: ${color}; margin-right: 8px; border-radius: 2px;"></span>
              <span style="font-weight: 500;">${param.seriesName}:</span>
              <span style="margin-left: 8px; font-weight: bold;">${value}</span>
            </div>
          `;
        });
        return result;
      },
    },
    legend: {
      data: ['Yêu cầu', 'Quà đã tặng', 'Giá trị (VNĐ)'],
      top: 30,
      textStyle: {
        color: '#6b7280',
      },
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '3%',
      containLabel: true,
    },
    xAxis: [
      {
        type: 'category',
        data: months,
        axisPointer: {
          type: 'shadow',
        },
        axisLabel: {
          color: '#6b7280',
          rotate: 45,
        },
      },
    ],
    yAxis: [
      {
        type: 'value',
        name: 'Số lượng',
        min: 0,
        axisLabel: {
          color: '#6b7280',
          formatter: '{value}',
        },
        splitLine: {
          lineStyle: {
            color: '#e5e7eb',
          },
        },
      },
      {
        type: 'value',
        name: 'Giá trị (VNĐ)',
        min: 0,
        axisLabel: {
          color: '#6b7280',
          formatter: function (value: number) {
            return currencyFormat(value).replace('₫', '');
          },
        },
        splitLine: {
          show: false,
        },
      },
    ],
    series: [
      {
        name: 'Yêu cầu',
        type: 'bar',
        data: requests,
        itemStyle: {
          color: '#3b82f6',
        },
        emphasis: {
          itemStyle: {
            color: '#2563eb',
          },
        },
      },
      {
        name: 'Quà đã tặng',
        type: 'bar',
        data: giftsGiven,
        itemStyle: {
          color: '#10b981',
        },
        emphasis: {
          itemStyle: {
            color: '#059669',
          },
        },
      },
      {
        name: 'Giá trị (VNĐ)',
        type: 'line',
        yAxisIndex: 1,
        data: values,
        itemStyle: {
          color: '#8b5cf6',
        },
        lineStyle: {
          color: '#8b5cf6',
          width: 3,
        },
        symbol: 'circle',
        symbolSize: 8,
      },
    ],
  };
};

const GiftRequestChart = ({
  selectedYear,
  onYearChange,
}: GiftRequestChartProps) => {
  const years = Array.from(
    { length: 5 },
    (_, i) => new Date().getFullYear() - i
  );

  const {
    data,
    isLoading: loading,
    error,
  } = useGiftRequestStatistics(selectedYear);

  if (loading) {
    return (
      <div className="bg-gradient-to-br from-white to-luxury-50/30 rounded-xl shadow-lg border border-luxury-200/50 overflow-hidden w-full">
        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 p-4 text-white">
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-white/20 rounded-lg">
              <Gift className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-lg font-bold">
                Thống Kê Yêu Cầu Quà Tặng theo năm
              </h2>
              <p className="text-purple-100 text-xs">
                Phân tích yêu cầu và quà tặng theo năm
              </p>
            </div>
          </div>
        </div>
        <div className="p-4">
          <div className="flex items-center justify-center h-64">
            <LoadingCircle />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-gradient-to-br from-white to-luxury-50/30 rounded-xl shadow-lg border border-luxury-200/50 overflow-hidden w-full">
        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 p-4 text-white">
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-white/20 rounded-lg">
              <Gift className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-lg font-bold">Thống Kê Yêu Cầu Quà Tặng</h2>
              <p className="text-purple-100 text-xs">
                Phân tích yêu cầu và quà tặng theo năm
              </p>
            </div>
          </div>
        </div>
        <div className="p-4">
          <div className="flex items-center justify-center h-64 text-luxury-600">
            {error.message || 'Không thể tải dữ liệu'}
          </div>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="bg-gradient-to-br from-white to-luxury-50/30 rounded-xl shadow-lg border border-luxury-200/50 overflow-hidden w-full">
        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 p-4 text-white">
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-white/20 rounded-lg">
              <Gift className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-lg font-bold">Thống Kê Yêu Cầu Quà Tặng</h2>
              <p className="text-purple-100 text-xs">
                Phân tích yêu cầu và quà tặng theo năm
              </p>
            </div>
          </div>
        </div>
        <div className="p-4">
          <div className="flex items-center justify-center h-64 text-luxury-600">
            Không có dữ liệu
          </div>
        </div>
      </div>
    );
  }

  const totalRequests = data.totalRequests;
  const totalGiftsGiven = data.totalGiftsGiven;
  const totalValue = data.totalValue;
  const averageValuePerGift = data.averageValuePerGift;

  return (
    <div className="bg-gradient-to-br from-white to-luxury-50/30 rounded-xl shadow-lg border border-luxury-200/50 overflow-hidden w-full">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 p-4 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-white/20 rounded-lg">
              <Gift className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-lg font-bold">Thống Kê Yêu Cầu Quà Tặng</h2>
              <p className="text-purple-100 text-xs">
                Phân tích yêu cầu và quà tặng theo năm
              </p>
            </div>
          </div>
          <div className="min-w-[100px]">
            <Select
              value={selectedYear.toString()}
              onValueChange={(value) => onYearChange(parseInt(value))}
            >
              <SelectTrigger className="w-full luxury-input bg-white/20 backdrop-blur-sm border-white/30 hover:border-white/50 transition-all duration-200 h-9 text-white">
                <SelectValue placeholder="Năm" />
              </SelectTrigger>
              <SelectContent className="border-luxury-200 bg-white/95 backdrop-blur-sm">
                {years.map((year) => (
                  <SelectItem
                    key={year}
                    value={year.toString()}
                    className="hover:bg-luxury-50"
                  >
                    {year}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Statistics Overview */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 p-3 sm:p-4">
        <div className="bg-gradient-to-br from-blue-50 to-blue-100/50 rounded-lg p-4 flex flex-col items-center border border-blue-200/50 shadow-md hover:shadow-lg transition-all duration-300 group">
          <div className="p-2 bg-blue-500/10 rounded-full mb-2 group-hover:scale-110 transition-transform duration-200">
            <Gift className="w-5 h-5 text-blue-600" />
          </div>
          <span className="text-gray-600 text-xs font-medium mb-1">
            Tổng yêu cầu
          </span>
          <span className="text-2xl font-bold text-blue-700 group-hover:text-blue-800 transition-colors">
            {numberFormat(totalRequests)}
          </span>
        </div>
        <div className="bg-gradient-to-br from-green-50 to-green-100/50 rounded-lg p-4 flex flex-col items-center border border-green-200/50 shadow-md hover:shadow-lg transition-all duration-300 group">
          <div className="p-2 bg-green-500/10 rounded-full mb-2 group-hover:scale-110 transition-transform duration-200">
            <Gift className="w-5 h-5 text-green-600" />
          </div>
          <span className="text-gray-600 text-xs font-medium mb-1">
            Quà đã tặng
          </span>
          <span className="text-2xl font-bold text-green-700 group-hover:text-green-800 transition-colors">
            {numberFormat(totalGiftsGiven)}
          </span>
        </div>
        <div className="bg-gradient-to-br from-purple-50 to-purple-100/50 rounded-lg p-4 flex flex-col items-center border border-purple-200/50 shadow-md hover:shadow-lg transition-all duration-300 group">
          <div className="p-2 bg-purple-500/10 rounded-full mb-2 group-hover:scale-110 transition-transform duration-200">
            <DollarSign className="w-5 h-5 text-purple-600" />
          </div>
          <span className="text-gray-600 text-xs font-medium mb-1">
            Tổng giá trị
          </span>
          <span className="text-lg font-bold text-purple-700 group-hover:text-purple-800 transition-colors">
            {currencyFormat(totalValue)}
          </span>
        </div>
        <div className="bg-gradient-to-br from-orange-50 to-orange-100/50 rounded-lg p-4 flex flex-col items-center border border-orange-200/50 shadow-md hover:shadow-lg transition-all duration-300 group">
          <div className="p-2 bg-orange-500/10 rounded-full mb-2 group-hover:scale-110 transition-transform duration-200">
            <TrendingUp className="w-5 h-5 text-orange-600" />
          </div>
          <span className="text-gray-600 text-xs font-medium mb-1">TB/quà</span>
          <span className="text-lg font-bold text-orange-700 group-hover:text-orange-800 transition-colors">
            {currencyFormat(averageValuePerGift)}
          </span>
        </div>
      </div>

      {/* Chart */}
      <div className="p-3 sm:p-4">
        <div className="bg-white/50 backdrop-blur-sm rounded-lg p-3 border border-luxury-200/50">
          <h3 className="text-lg font-semibold text-luxury-900 mb-4">
            Phân Bố Yêu Cầu Quà Tặng Theo Tháng
          </h3>
          <div className="h-[400px]">
            <ReactECharts
              option={getGiftRequestChartOption(data.monthlyData)}
              style={{ height: 400 }}
              notMerge={true}
              lazyUpdate={true}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default GiftRequestChart;
